import {
  CreateListResponse,
  AddReportToListResponse,
} from '@/dashboard/AccountLists/useAccountLists/useAccountLists';
import { ListPropertyParams } from '@/dashboard/AccountListsV2/hooks/interfaces';
import { RecentReportRawData } from '@/recentReports/Interfaces';
import { LizanoListInfo } from '@/reports/sections/ListsSection/lists/lists';
import { ReportNote } from '@/reports/sections/NotesSection/Notes/Notes';
import {
  AddReportToListV2AddressPayload,
  SerializedBulkSearchProperty,
} from '@/reports/types/serialized';
import { request } from '@/utils/index';

export interface AccountListRequestsInterface {
  getAccountListsRequest: () => Promise<LizanoListInfo[]>;
  getAccountListsV2Request: (token: string) => Promise<LizanoListInfo[]>;
  createListRequest: (params: CreateListParams) => Promise<CreateListResponse>;
  updateListRequest: (
    updateParams: UpdateListParams
  ) => Promise<LizanoListInfo>;
  addReportToListRequest: (
    list_code: string,
    permaLink: string
  ) => Promise<AddReportToListResponse>;
  addReportToListPropertiesRequest: (
    listCode: string,
    property: AddReportToListV2AddressPayload,
    token: string
  ) => Promise<unknown>;
  addReportToListItemsRequest: (
    listCode: string,
    item: AddReportToListV2AddressPayload,
    reportType: string,
    token: string
  ) => Promise<unknown>;
  removeReportFromListRequest: (
    list_code: string,
    lisReportId: number
  ) => Promise<unknown>;
  deleteListRequest: (listCode: string) => Promise<unknown>;
  bulkAddListPropertyRequest: (
    listCode: string,
    properties: ListPropertyParams[],
    token: string
  ) => Promise<unknown>;
  bulkAddListItemRequest: (
    listCode: string,
    items: ListPropertyParams[],
    token: string
  ) => Promise<unknown>;
  bulkSearchListPropertyRequest: (
    properties: SerializedBulkSearchProperty
  ) => Promise<unknown>;
  bulkSearchListItemRequest: (
    items: SerializedBulkSearchProperty
  ) => Promise<unknown>;
  bulkMoveListPropertyRequest: (
    listCode: string,
    propertyIDs: string[],
    token: string
  ) => Promise<unknown>;
  bulkMoveListItemRequest: (
    listCode: string,
    itemIDs: string[],
    token: string
  ) => Promise<unknown>;
  bulkDeleteListPropertyRequest: (
    listCode: string,
    propertyIDs: string[],
    token: string
  ) => Promise<unknown>;
  bulkDeleteListItemRequest: (
    listCode: string,
    itemIDs: string[],
    token: string
  ) => Promise<unknown>;
}

interface UpdateListParams {
  name: string;
  listCode?: string;
}

interface listParams {
  name: string;
  listCode?: string;
}

interface CreateListParams {
  name: string;
  permalink: string;
}

export interface LizanoMetaData {
  id: number;
  list_id: number;
  permalink: string;
  created_at: string;
  updated_at: string;
  report: RecentReportRawData;
}

export interface LizanoReportResponse {
  created_at: string;
  updated_at: string;
  report_type: string;
  alert_me: boolean | null;
  permalink: string;
  tag: string | null;
  rating: string | null;
  interaction?: LizanoInteractions;
  has_new_info: boolean;
  no_entities: unknown;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  notes?: ReportNote[];
}

export interface LizanoListResponse extends LizanoReportResponse {
  lists: LizanoListInfo[];
}

interface LizanoInteractions {
  opened: boolean;
  opened_at: string;
}

const LISTS_V2_URL = '/ms/lizano/api/v2/lists';

export const orderLists = (lists: LizanoListInfo[]) => {
  const savedReports = lists.find((list: LizanoListInfo) => {
    if (list.name === 'saved reports') {
      return list;
    }
  });
  const otherLists = lists.filter((list: LizanoListInfo) => {
    if (list.name !== 'saved reports') {
      return list;
    }
  });
  if (savedReports && otherLists) {
    return [savedReports, ...otherLists];
  }
  return lists;
};

const getAccountListsRequest = async () => {
  const response: LizanoListResponse = await request('/api/v5/lizano/lists', {
    method: 'GET',
    headers: {
      'content-type': 'application/json',
    },
  });
  if (!response.lists) return [];
  return orderLists(response.lists);
};

const getAccountListsV2Request = async (token: string) => {
  const response: LizanoListResponse = await request(LISTS_V2_URL, {
    method: 'GET',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.lists) return [];
  return orderLists(response.lists);
};

const createListRequest = async (params: CreateListParams) => {
  const obj = { name: params.name };
  const data = await request('/api/v5/lizano/lists', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(obj),
  });
  return { data, permalink: params.permalink };
};

const updateListRequest = async (updateParams: listParams) => {
  const { name } = updateParams;
  return request(`/api/v5/lizano/lists/${updateParams.listCode}`, {
    method: 'PUT',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ name }),
  });
};

const addReportToListRequest = async (list_code: string, permaLink: string) => {
  const newReport = {
    permalink: permaLink,
    list_code,
  };
  return request(`/api/v5/lizano/lists/${list_code}/report`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(newReport),
  });
};

const addReportToListPropertiesRequest = async (
  listCode: string,
  property: AddReportToListV2AddressPayload,
  token: string
) => {
  const payload = { property };
  return request(`${LISTS_V2_URL}/${listCode}/list_properties`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
};

const addReportToListItemsRequest = async (
  listCode: string,
  item: AddReportToListV2AddressPayload,
  reportType: string,
  token: string
) => {
  const payload = {
    item: {
      ownable_id: '',
      blacklisted: false,
      report_type: reportType,
      item_data: {
        ...item,
        blacklisted: false,
      },
    },
  };
  return request(`${LISTS_V2_URL}/${listCode}/list_items`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
};

const removeReportFromListRequest = async (
  list_code: string,
  lisReportId: number
) => {
  const report = {
    list_code,
    reports: lisReportId,
  };
  return request(`/api/v5/lizano/lists/${list_code}/reports`, {
    method: 'DELETE',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(report),
  });
};

const deleteListRequest = async (listCode: string) => {
  return request(`/api/v5/lizano/lists/${listCode}`, {
    method: 'DELETE',
    headers: {
      'content-type': 'application/json',
    },
  });
};

export const bulkAddListPropertyRequest = async (
  listCode: string,
  properties: ListPropertyParams[],
  token: string
) => {
  return request(`${LISTS_V2_URL}/${listCode}/list_properties/bulk_create`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ list_properties: properties }),
  });
};

export const bulkAddListItemRequest = async (
  listCode: string,
  items: ListPropertyParams[],
  token: string
) => {
  return request(`${LISTS_V2_URL}/${listCode}/list_items/bulk_create`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ list_items: items }),
  });
};

const bulkMoveListPropertyRequest = async (
  listCode: string,
  propertyIDs: string[],
  token: string
) => {
  return request(`${LISTS_V2_URL}/${listCode}/list_properties/bulk_move`, {
    method: 'PATCH',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ list_property_ids: propertyIDs }),
  });
};

const bulkMoveListItemRequest = async (
  listCode: string,
  itemIDs: string[],
  token: string
) => {
  return request(`${LISTS_V2_URL}/${listCode}/list_items/bulk_move`, {
    method: 'PATCH',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ list_property_ids: itemIDs }),
  });
};

const bulkDeleteListPropertyRequest = async (
  listCode: string,
  propertyIDs: string[],
  token: string
) => {
  return request(`${LISTS_V2_URL}/${listCode}/list_properties/bulk_delete`, {
    method: 'DELETE',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ list_property_ids: propertyIDs }),
  });
};

const bulkDeleteListItemRequest = async (
  listCode: string,
  itemIDs: string[],
  token: string
) => {
  return request(`${LISTS_V2_URL}/${listCode}/list_items/bulk_delete`, {
    method: 'DELETE',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ list_property_ids: itemIDs }),
  });
};

const bulkSearchListPropertyRequest = async (
  data: SerializedBulkSearchProperty
) => {
  return request('/api/v5/reports/bulk', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(data),
  });
};

const bulkSearchListItemRequest = async (
  data: SerializedBulkSearchProperty
) => {
  return request('/api/v5/reports/bulk', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(data),
  });
};

export const AccountListRequests = {
  getAccountListsRequest,
  getAccountListsV2Request,
  createListRequest,
  updateListRequest,
  addReportToListRequest,
  addReportToListPropertiesRequest,
  addReportToListItemsRequest,
  removeReportFromListRequest,
  deleteListRequest,
  bulkSearchListPropertyRequest,
  bulkSearchListItemRequest,
  bulkAddListPropertyRequest,
  bulkAddListItemRequest,
  bulkMoveListPropertyRequest,
  bulkMoveListItemRequest,
  bulkDeleteListPropertyRequest,
  bulkDeleteListItemRequest,
};
